import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';

// Helper to get auth header
function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

export default function AshleyInventory() {
  const { selectedLocation } = useLocationContext();
  const locationId = selectedLocation?.id || selectedLocation?._id || selectedLocation || null;

  const [status, setStatus] = useState({ loading: false, error: '', success: '' });
  const [catalog, setCatalog] = useState(null);

  // Search state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const pages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);
  // CSV filenames input (comma-separated)
  const [csvFiles, setCsvFiles] = useState('');

  // Load ashley catalog for current location
  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    async function loadCatalog() {
      try {
        setStatus(s => ({ ...s, error: '' }));
        const res = await fetch(`/api/integrations/external-catalogs`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Failed to load catalogs (${res.status})`);
        const json = await res.json();
        const items = json?.data || json?.catalogs || [];
        const match = items.find(c => c.provider === 'ashley' && c.locationId === locationId);
        if (!cancelled) setCatalog(match || null);
      } catch (e) {
        if (!cancelled) setStatus(s => ({ ...s, error: e.message }));
      }
    }
    loadCatalog();
    return () => { cancelled = true; };
  }, [locationId]);

  // Load products
  async function fetchProducts(params = {}) {
    if (!locationId) return;
    const q = new URLSearchParams({
      locationId,
      provider: 'ashley',
      search: params.search ?? search ?? '',
      page: String(params.page ?? page),
      limit: String(params.limit ?? limit),
    });
    setStatus(s => ({ ...s, loading: true, error: '' }));
    try {
      const res = await fetch(`/api/integrations/external-products/search?${q.toString()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      const json = await res.json();
      const data = json?.data || {};
      setRows(data.products || []);
      setTotal(data.pagination?.total || 0);
    } catch (e) {
      setStatus(s => ({ ...s, error: e.message }));
    } finally {
      setStatus(s => ({ ...s, loading: false }));
    }
  }

  useEffect(() => {
    setPage(1); // reset page when location or search changes
  }, [locationId]);

  useEffect(() => {
    fetchProducts({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  // Trigger CSV import for current location's ashley catalog
  async function handleImport() {
    if (!catalog?.id) {
      setStatus(s => ({ ...s, error: 'No Ashley catalog found for this location.' }));
      return;
    }
    setStatus({ loading: true, error: '', success: '' });
    try {
      // Build request body from csvFiles input
      let body = {};
      const trimmed = (csvFiles || '').trim();
      if (trimmed) {
        const files = trimmed.split(',').map(s => s.trim()).filter(Boolean);
        if (files.length === 1) body.file = files[0];
        if (files.length > 1) body.files = files;
      }
      const res = await fetch(`/api/integrations/external-catalogs/${catalog.id}/import/shopify-local`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`Import failed (${res.status})`);
      const json = await res.json();
      setStatus({ loading: false, error: '', success: `Imported ${json?.data?.imported ?? 0} products.` });
      fetchProducts({ page: 1 });
    } catch (e) {
      setStatus({ loading: false, error: e.message, success: '' });
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Ashley's Inventory</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="products_export_1.csv, products_export_2.csv"
            value={csvFiles}
            onChange={(e) => setCsvFiles(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={handleImport} disabled={!catalog || status.loading}>
            Import CSVs
          </Button>
          <IconButton onClick={() => fetchProducts()} disabled={status.loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchProducts({ page: 1, search: e.target.value }); } }}
              placeholder="Search by name, SKU, or description"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Button variant="outlined" onClick={() => { setPage(1); fetchProducts({ page: 1 }); }} disabled={status.loading}>
              Search
            </Button>
          </Stack>
          {status.error && (
            <Typography color="error" sx={{ mt: 1 }}>{status.error}</Typography>
          )}
          {status.success && (
            <Typography color="success.main" sx={{ mt: 1 }}>{status.success}</Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">MSRP</TableCell>
                  <TableCell align="right">Wholesale</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status.loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No products</TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={`${row.catalogId}-${row.externalId}`} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.sku || '-'}</TableCell>
                      <TableCell>{row.brand || '-'}</TableCell>
                      <TableCell>{row.category || '-'}</TableCell>
                      <TableCell align="right">{row.msrpPrice ?? '-'}</TableCell>
                      <TableCell align="right">{row.wholesalePrice ?? '-'}</TableCell>
                      <TableCell>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="body2">Total: {total}</Typography>
            <Pagination
              page={page}
              count={pages}
              color="primary"
              onChange={(_, p) => { setPage(p); fetchProducts({ page: p }); }}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
